import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ThumbsUp, MessageSquare, ShieldCheck, Pin, Store } from "lucide-react";
import { shopApi } from "@/lib/api";
import { useAuth } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function QASection({ product }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: questions = [] } = useQuery({
    queryKey: ["qa", product.product_id],
    queryFn: () => shopApi.listQuestions(product.product_id),
  });
  const [askText, setAskText] = useState("");
  const [posting, setPosting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const ask = async () => {
    if (!askText.trim()) return;
    if (!user) { toast.error("Sign in to ask a question"); return; }
    setPosting(true);
    try {
      await shopApi.askQuestion(product.product_id, askText.trim());
      setAskText(""); setShowForm(false);
      qc.invalidateQueries({ queryKey: ["qa", product.product_id] });
      toast.success("Question posted");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to post"); }
    setPosting(false);
  };

  const vote = async (kind, id) => {
    if (!user) { toast.error("Sign in to vote"); return; }
    try {
      if (kind === "q") await shopApi.voteQuestion(id);
      else await shopApi.voteAnswer(id);
      qc.invalidateQueries({ queryKey: ["qa", product.product_id] });
    } catch { toast.error("Failed"); }
  };

  return (
    <section className="mt-16" data-testid="qa-section">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Questions & answers</h2>
          <p className="text-sm text-muted-foreground mt-1">{questions.length} questions · sorted by most helpful</p>
        </div>
        <Button className="btn-primary" onClick={() => user ? setShowForm((v) => !v) : toast.error("Sign in to ask a question")} data-testid="ask-question-btn">
          <MessageSquare className="h-4 w-4 mr-2" /> Ask a question
        </Button>
      </div>

      {showForm && (
        <div className="rounded-2xl bg-card border border-white/5 p-4 mb-4" data-testid="ask-question-form">
          <Textarea rows={3} value={askText} onChange={(e) => setAskText(e.target.value)} placeholder="What do you want to know about this product?" data-testid="ask-question-textarea" />
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" className="border-white/15" onClick={() => { setShowForm(false); setAskText(""); }}>Cancel</Button>
            <Button className="btn-primary" onClick={ask} disabled={posting || !askText.trim()} data-testid="submit-question">{posting ? "Posting…" : "Post question"}</Button>
          </div>
        </div>
      )}

      {questions.length === 0 ? (
        <p className="text-muted-foreground text-sm">No questions yet. Be the first to ask.</p>
      ) : (
        <div className="space-y-3">
          {questions.map((q) => <QuestionBlock key={q.question_id} q={q} onVote={vote} product={product} />)}
        </div>
      )}
    </section>
  );
}

function QuestionBlock({ q, onVote, product }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [ansText, setAnsText] = useState("");
  const [answering, setAnswering] = useState(false);
  const [showAns, setShowAns] = useState(false);

  const submit = async () => {
    if (!ansText.trim()) return;
    if (!user) { toast.error("Sign in to answer"); return; }
    setAnswering(true);
    try {
      await shopApi.answerQuestion(q.question_id, ansText.trim());
      setAnsText(""); setShowAns(false);
      qc.invalidateQueries({ queryKey: ["qa", product.product_id] });
      toast.success("Answer posted");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setAnswering(false);
  };

  return (
    <div className="rounded-2xl bg-card border border-white/5 p-5" data-testid={`question-${q.question_id}`}>
      <div className="flex gap-3">
        <button onClick={() => onVote("q", q.question_id)} className="flex flex-col items-center gap-0.5 text-xs text-muted-foreground hover:text-primary" data-testid={`vote-question-${q.question_id}`}>
          <ThumbsUp className="h-4 w-4" />
          <span>{q.upvotes || 0}</span>
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
            <span className="font-medium text-white">{q.user_name}</span>
            {q.pinned && <span className="chip text-primary border-primary/30"><Pin className="h-3 w-3" /> Pinned</span>}
            <span>· {new Date(q.created_at).toLocaleDateString()}</span>
          </div>
          <p className="text-sm">{q.text}</p>

          {q.answers?.length > 0 && (
            <div className="mt-4 space-y-3 pl-4 border-l border-white/10">
              {q.answers.map((a) => (
                <div key={a.answer_id} className="flex gap-3" data-testid={`answer-${a.answer_id}`}>
                  <button onClick={() => onVote("a", a.answer_id)} className="flex flex-col items-center gap-0.5 text-xs text-muted-foreground hover:text-primary" data-testid={`vote-answer-${a.answer_id}`}>
                    <ThumbsUp className="h-3 w-3" />
                    <span>{a.upvotes || 0}</span>
                  </button>
                  <div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                      <span className="font-medium text-white">{a.user_name}</span>
                      {a.staff && <span className="chip text-primary border-primary/30"><Store className="h-3 w-3" /> Store staff</span>}
                      {a.verified_buyer && !a.staff && <span className="chip text-primary border-primary/30"><ShieldCheck className="h-3 w-3" /> Verified buyer</span>}
                    </div>
                    <p className="text-sm">{a.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-3">
            {showAns ? (
              <div>
                <Textarea rows={2} value={ansText} onChange={(e) => setAnsText(e.target.value)} placeholder="Share what you know…" data-testid={`answer-input-${q.question_id}`} />
                <div className="flex justify-end gap-2 mt-2">
                  <Button variant="outline" size="sm" className="border-white/15" onClick={() => { setShowAns(false); setAnsText(""); }}>Cancel</Button>
                  <Button size="sm" className="btn-primary" onClick={submit} disabled={answering || !ansText.trim()} data-testid={`submit-answer-${q.question_id}`}>{answering ? "Posting…" : "Post answer"}</Button>
                </div>
              </div>
            ) : (
              <button onClick={() => user ? setShowAns(true) : toast.error("Sign in to answer")} className="text-xs text-primary hover:underline" data-testid={`show-answer-form-${q.question_id}`}>Answer this question</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
